import { useState, useEffect } from "react";
import { Square, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { LiveSession } from "@/lib/types";

type Props = {
  session: LiveSession;
  onEnd: () => void;
  onDelete?: () => void;
};

const providerLabels: Record<string, string> = {
  "claude-code": "Claude Code",
  codex: "Codex",
  gemini: "Gemini",
};

function useElapsedTime(startedAt: string, endedAt: string | null) {
  const [elapsed, setElapsed] = useState("");

  useEffect(() => {
    function update() {
      const start = new Date(startedAt).getTime();
      const end = endedAt ? new Date(endedAt).getTime() : Date.now();
      const diff = Math.max(0, Math.floor((end - start) / 1000));

      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;

      if (hours > 0) {
        setElapsed(
          `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
        );
      } else {
        setElapsed(
          `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
        );
      }
    }

    update();
    if (!endedAt) {
      const interval = setInterval(update, 1000);
      return () => clearInterval(interval);
    }
  }, [startedAt, endedAt]);

  return elapsed;
}

export function SessionHeader({ session, onEnd, onDelete }: Props) {
  const isActive =
    session.status === "active" || session.status === "starting";
  const elapsed = useElapsedTime(session.started_at, session.ended_at);
  const title = session.title || session.repo || "New Session";

  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800 bg-zinc-900 shrink-0">
      {/* Status dot */}
      <div className="relative shrink-0">
        <div
          className={cn(
            "h-2.5 w-2.5 rounded-full",
            isActive ? "bg-green-500" : "bg-zinc-500"
          )}
        />
        {isActive && (
          <div className="absolute inset-0 h-2.5 w-2.5 rounded-full bg-green-500 animate-ping opacity-75" />
        )}
      </div>

      {/* Title + provider */}
      <div className="flex-1 min-w-0">
        <h2 className="text-sm font-semibold text-zinc-100 truncate">
          {title}
        </h2>
        <div className="flex items-center gap-2 mt-0.5">
          <Badge
            variant="secondary"
            className="text-[10px] px-1.5 py-0 h-4 bg-zinc-800 text-zinc-400 border-zinc-700"
          >
            {providerLabels[session.provider] ?? session.provider}
          </Badge>
          <span className="text-[10px] text-zinc-500 font-mono">{elapsed}</span>
        </div>
      </div>

      {/* End button */}
      {isActive && (
        <Button
          size="sm"
          variant="destructive"
          onClick={onEnd}
          className="shrink-0 gap-1.5 h-8"
        >
          <Square className="h-3.5 w-3.5" />
          <span className="hidden sm:inline text-xs">End</span>
        </Button>
      )}
      {!isActive && onDelete && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          className="shrink-0 h-8 w-8 p-0 text-zinc-500 hover:text-red-400"
          title="Delete session"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
